var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Tabs, Tab, Skeleton, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GridFilters } from "@ui/components/grid/GridFilters";
import { PrimaryGrid } from "@ui/components/grid/PrimaryGrid";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { TemperatureStatusBadge, getNodeTemperatureColor, getNodeTemperatureStatus, } from "@ui/components/temperature";
import StatusIndicator from "@ui/components/status/StatusIndicator";
import { SButton } from "@ui/components/buttons/SButton";
import { colCenter } from "@ui/components/style/style";
import { ConnectedIcon, DisconnectedIcon, } from "@ui/components/icons/ConnectionIcons";
import { GridCellStack } from "@ui/components/grid";
import { FreeRoamingBadge } from "@ui/components/badges/FreeRoamingBadge";
import { formatUtcToLocalDate, formatUtcToLocalTime, } from "@application/common/helpers/date-util";
import { getLatestApActivityIso } from "@utility/access-point-monitoring";
import { APDetailsCard } from "@ui/pages/dashboard/ap-details/components/APDetailsCard";
import { NodeActionsDialog } from "@ui/pages/dashboard/ap-details/components/NodeActionsDialog";
import { APDetailsCardSkeleton } from "@ui/pages/dashboard/ap-details/components/APDetailsCardSkeleton";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAccessPoint } from "@reducers/access-points/thunk/access-point-thunk";
import { useTranslation } from "react-i18next";
import { getNodesByAccessPointUid, getAllNodes, forceConnectNode, forceDisconnectNode, } from "@reducers/nodes/thunk/nodes-thunk";
import { NodeBehaviour } from "@reducers/nodes/models/nodes-dto";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { useDebounce } from "@ui/utils/useDebounce";
import { cleanNodesFiltersForApi } from "@ui/pages/dashboard/ap-details/cleanNodesFiltersForApi";
export const EmbeddedAPProfile = ({ apId, onClose, }) => {
    const { t } = useTranslation("common");
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // Get AP data from Redux
    const { selectedAccessPoint, singleLoading, singleError } = useAppSelector((state) => state.accessPoint);
    // Get nodes data from Redux
    const { nodes, loading: nodesLoading, paginationInfo, } = useAppSelector((state) => state.nodes);
    // State for dialogs
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    // Tab and filters state - declared before useEffect that uses tabValue
    const [tabValue, setTabValue] = useState("connected");
    const [filters, setFilters] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const cleanFilters = useCallback(cleanNodesFiltersForApi, []);
    const nodesFetchKeyRef = useRef(null);
    // Fetch AP once
    useEffect(() => {
        if (apId) {
            dispatch(getAccessPoint(apId));
        }
    }, [dispatch, apId]);
    // Fetch nodes when tab, filters, or search change
    useEffect(() => {
        if (!apId)
            return;
        const apiFilters = cleanFilters(filters);
        if (debouncedSearchTerm.trim()) {
            apiFilters.globalSearch = debouncedSearchTerm.trim();
        }
        const fetchKey = JSON.stringify({
            apId,
            tabValue,
            filters,
            debouncedSearchTerm,
        });
        if (nodesFetchKeyRef.current === fetchKey)
            return;
        nodesFetchKeyRef.current = fetchKey;
        if (tabValue === "disconnected") {
            dispatch(getAllNodes({
                pageNumber: 1,
                pageSize: 1000,
                filters: Object.assign(Object.assign({}, apiFilters), { simulatedDisconnectedOnly: true }),
            }));
        }
        else {
            dispatch(getNodesByAccessPointUid({
                accessPointUid: apId,
                query: {
                    pageNumber: 1,
                    pageSize: 100,
                    filters: apiFilters,
                },
            }));
        }
    }, [dispatch, apId, tabValue, filters, debouncedSearchTerm, cleanFilters]);
    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);
    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);
    // Transform API nodes data to grid format
    const apNodes = React.useMemo(() => {
        if (!nodes)
            return [];
        return nodes.map((node) => {
            var _a, _b;
            return ({
                id: node.id,
                temperature: node.nodeTemprature || 0,
                nodeTemperature: node.nodeTemprature,
                lowTemperature: node.lowTemperature,
                highTemperature: node.highTemperature,
                leftIndicator: node.nodeTypeCode === 1 ? 1 : undefined,
                rightIndicator: node.nodeTypeCode === 1 ? 2 : undefined,
                nodeName: node.nodeName,
                nodeId: node.nodeUid,
                freeRoamingNode: (_a = node.freeRoamingNode) !== null && _a !== void 0 ? _a : false,
                isActive: node.isActive,
                status: node.isActive && node.nodeBehaviour === NodeBehaviour.Normal
                    ? "Active"
                    : "Inactive",
                deviceName: node.accessPointName || "",
                deviceId: node.accessPointUid || "",
                lastUpdate: node.lastUpdated,
                nodeBehaviour: (_b = node.nodeBehaviour) !== null && _b !== void 0 ? _b : NodeBehaviour.Normal,
            });
        });
    }, [nodes]);
    const columns = [
        {
            field: "temperature",
            headerName: t("grid.ap"),
            minWidth: 100,
            filterable: false,
            type: "string",
            renderCell: (params) => (React.createElement(TemperatureDisplay, { width: 91, temperature: params.row.nodeTemperature, color: getNodeTemperatureColor(params.row), leftIndicator: params.row.leftIndicator, rightIndicator: params.row.rightIndicator })),
        },
        {
            field: "nodeName",
            headerName: t("grid.nameId"),
            flex: 1.5,
            minWidth: 180,
            filterable: true,
            filterType: "text",
            type: "string",
            renderCell: (params) => (
            // width: 100% is what lets the name box below actually claim the leftover
            // width, so the badge lands at the same x position on every row.
            React.createElement(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    height: "100%",
                    width: "100%",
                    minWidth: 0,
                } },
                React.createElement(Box, { sx: { minWidth: 0, flex: 1 } },
                    React.createElement(GridCellStack, { topText: params.row.nodeName, bottomText: params.row.nodeId })),
                params.row.freeRoamingNode && React.createElement(FreeRoamingBadge, null))),
        },
        {
            field: "temperature",
            headerName: t("grid.temperature"),
            minWidth: 100,
            filterable: false,
            type: "string",
            renderCell: (params) => (React.createElement(Box, { sx: { height: "100%", display: "flex", alignItems: "center" } },
                React.createElement(TemperatureStatusBadge, { status: getNodeTemperatureStatus(params.row) }))),
        },
        {
            field: "status",
            headerName: t("grid.status"),
            flex: 1,
            minWidth: 115,
            filterable: true,
            filterType: "select",
            filterOptions: ["Active", "Inactive"],
            type: "string",
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.status === "Active" ? "active" : "inactive" })),
        },
        {
            field: "nodeBehaviour",
            headerName: t("grid.nodeBehaviour", "Behaviour"),
            minWidth: 140,
            filterable: false,
            type: "string",
            renderCell: (params) => {
                var _a;
                const behaviour = (_a = params.row.nodeBehaviour) !== null && _a !== void 0 ? _a : NodeBehaviour.Normal;
                const label = behaviour === NodeBehaviour.NoNodeCommunication
                    ? t("grid.noNodeCommunication", "No Node Communication")
                    : t("grid.normal", "Normal");
                return (React.createElement(StatusIndicator, { variant: behaviour === NodeBehaviour.NoNodeCommunication
                        ? "inactive"
                        : "active", label: label }));
            },
        },
        {
            field: "lastUpdate",
            headerName: t("grid.lastUpdate"),
            flex: 1.2,
            minWidth: 120,
            filterable: false,
            type: "string",
            renderCell: (params) => {
                const isNoCommunication = params.row.nodeBehaviour === NodeBehaviour.NoNodeCommunication;
                const errorColor = theme.palette.error.main;
                return (React.createElement(GridCellStack, Object.assign({ topText: formatUtcToLocalDate(params.row.lastUpdate), bottomText: formatUtcToLocalTime(params.row.lastUpdate) }, (isNoCommunication && {
                    topStyle: { color: errorColor },
                    bottomStyle: { color: errorColor },
                }))));
            },
        },
        {
            field: "actions",
            headerName: t("grid.actions"),
            minWidth: 270,
            filterable: false,
            type: "string",
            renderCell: (params) => {
                return (React.createElement(Box, { sx: { display: "flex", gap: 1 } },
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) &&
                        (tabValue === "disconnected" ? (React.createElement(SButton, { variant: "soft", color: "success", onClick: (e) => {
                                e.stopPropagation();
                                setSelectedNode(params.row);
                                setIsDialogOpen(true);
                            } }, t("actions.connect"))) : (React.createElement(SButton, { variant: "soft", color: "error", onClick: (e) => {
                                e.stopPropagation();
                                setSelectedNode(params.row);
                                setIsDialogOpen(true);
                            } }, t("actions.disconnect")))),
                    React.createElement(SButton, { variant: "soft", color: "primary", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/nodes/${params.row.nodeId}/logs/${apId}`);
                        } }, "Logs"),
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/nodes/${params.row.id}/edit`);
                        } }, t("actions.settings")))));
            },
        },
    ];
    // Disconnected list is fully server-filtered (simulated disconnected). Connected: hide rows without simulated AP if any slip through.
    const filteredNodes = useMemo(() => {
        if (!apId)
            return [];
        if (tabValue === "disconnected") {
            return apNodes;
        }
        return apNodes.filter((node) => { var _a; return Boolean((_a = node.deviceId) === null || _a === void 0 ? void 0 : _a.trim()); });
    }, [apNodes, tabValue, apId]);
    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setSelectedNode(null);
    };
    // Determine if current action is connect or disconnect based on tab
    const isConnectAction = selectedNode && tabValue === "disconnected";
    const handleNodeAction = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedNode || !apId)
            return;
        try {
            if (isConnectAction) {
                // Handle force connect
                yield dispatch(forceConnectNode({
                    nodeId: selectedNode.id,
                    accessPointUid: apId,
                })).unwrap();
                // Small delay to ensure database update is complete, then refresh
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    setTabValue("connected");
                    const apiFilters = cleanNodesFiltersForApi(filters);
                    if (searchTerm.trim()) {
                        apiFilters.globalSearch = searchTerm.trim();
                    }
                    yield dispatch(getNodesByAccessPointUid({
                        accessPointUid: apId,
                        query: {
                            pageNumber: 1,
                            pageSize: 100,
                            filters: apiFilters,
                        },
                    })).unwrap();
                }), 200);
            }
            else {
                // Handle force disconnect
                yield dispatch(forceDisconnectNode({
                    nodeId: selectedNode.id,
                })).unwrap();
                // Small delay to ensure database update is complete, then refresh
                setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                    if (tabValue === "disconnected") {
                        // Refresh all disconnected nodes
                        const apiFilters = cleanNodesFiltersForApi(filters);
                        if (searchTerm.trim()) {
                            apiFilters.globalSearch = searchTerm.trim();
                        }
                        yield dispatch(getAllNodes({
                            pageNumber: 1,
                            pageSize: 1000,
                            filters: Object.assign(Object.assign({}, apiFilters), { simulatedDisconnectedOnly: true }),
                        })).unwrap();
                    }
                    else {
                        const apiFiltersConnected = cleanNodesFiltersForApi(filters);
                        if (searchTerm.trim()) {
                            apiFiltersConnected.globalSearch = searchTerm.trim();
                        }
                        yield dispatch(getNodesByAccessPointUid({
                            accessPointUid: apId,
                            query: {
                                pageNumber: 1,
                                pageSize: 100,
                                filters: apiFiltersConnected,
                            },
                        })).unwrap();
                    }
                }), 200);
            }
            handleDialogClose();
        }
        catch (error) {
            console.error(`Failed to ${isConnectAction ? "force connect" : "force disconnect"} node:`, error);
        }
    });
    // Show skeleton while loading
    if (singleLoading && !selectedAccessPoint) {
        return React.createElement(APDetailsCardSkeleton, null);
    }
    return (React.createElement(Box, null,
        selectedAccessPoint && (React.createElement(APDetailsCard, { name: (selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.accessPointName) || t("actions.unknownAp"), deviceId: (selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.accessPointUID) || apId || "", apId: apId, status: (selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.isActive) ? "active" : "inactive", lastUpdatedAt: selectedAccessPoint
                ? getLatestApActivityIso(selectedAccessPoint)
                : undefined, address: selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.address, place: selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.place, calibrationDate: selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.calibrationDate, onSettingsClick: () => navigate(`/dashboard/${apId}/edit`) })),
        nodesLoading && (!nodes || nodes.length === 0) ? (React.createElement(Box, { sx: { mt: 2 } },
            React.createElement(Skeleton, { variant: "text", width: 150, height: 32, sx: { mb: 1 } }),
            React.createElement(Skeleton, { variant: "rectangular", height: 300, sx: { borderRadius: 1 } }))) : (React.createElement(React.Fragment, null,
            React.createElement(GridFilters, { title: `${(paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) || filteredNodes.length} Nodes`, loading: nodesLoading, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, rightSection: React.createElement(Tabs, { value: tabValue, onChange: (_, v) => {
                        setTabValue(v);
                        setSearchTerm("");
                        setFilters({});
                        nodesFetchKeyRef.current = null;
                    }, sx: { minHeight: 40 } },
                    React.createElement(Tab, { value: "connected", label: React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                            React.createElement(ConnectedIcon, { width: 20, height: 20 }),
                            React.createElement("span", null, t("status.connected"))) }),
                    React.createElement(Tab, { value: "disconnected", label: React.createElement(Box, { sx: Object.assign(Object.assign({}, colCenter), { gap: 1 }) },
                            React.createElement(DisconnectedIcon, { width: 20, height: 20 }),
                            React.createElement("span", null, t("status.disconnected"))) })) }),
            React.createElement(PrimaryGrid, { rows: nodesLoading ? [] : filteredNodes, columns: columns, loading: nodesLoading, heightVariant: "dashboard", checkboxSelection: false, autoHeight: true, onRowClick: (params, event) => {
                    // Don't navigate if clicking on action buttons or columns
                    const target = event.target;
                    if (target.closest('[data-field="actions"]') ||
                        target.closest("button") ||
                        target.closest('[role="button"]')) {
                        return;
                    }
                    // Navigate to node logs (same as Dashboard behavior)
                    if (params.row.nodeId) {
                        navigate(`/nodes/${params.row.nodeId}/logs/${apId}`);
                    }
                } }))),
        React.createElement(NodeActionsDialog, { isOpen: isDialogOpen, onClose: handleDialogClose, onConfirm: handleNodeAction, selectedNode: selectedNode, action: isConnectAction ? "connect" : "disconnect", accessPointName: selectedAccessPoint === null || selectedAccessPoint === void 0 ? void 0 : selectedAccessPoint.accessPointName })));
};
